/**
 * Shared XML generation utilities for ADF (Auto-lead Data Format).
 * 
 * KEY PRINCIPLE: NO hardcoded values. All defaults come from:
 * - adf_elements.attributes (for attribute defaults)
 * - adf_elements.example_value (for content)
 * - adf_elements.alternate_example_value (for trade-in/co-buyer)
 */

/**
 * Attribute configuration from adf_elements.attributes JSONB
 */
export interface AttributeConfig {
  values?: string[];
  default?: string;
  required?: boolean;
  type?: string;
  description?: string;
}

/**
 * Options for building attribute strings
 */
export interface AttributeOptions {
  /** Include optional attributes (default: true) */
  includeOptional?: boolean;
  /** Use merge field placeholders instead of values */
  useMergeFields?: boolean;
  /** Element slug for merge field naming */
  elementSlug?: string;
  /** Vehicle interest override for trade-in scenarios */
  vehicleInterest?: 'buy' | 'trade-in';
  /** Co-buyer flag to set primarycontact to '0' */
  isCoBuyer?: boolean;
}

/**
 * Options for example content selection
 */
export interface ExampleContentOptions {
  /** Use alternate_example_value when available */
  useAlternate?: boolean;
}

/**
 * Element type for sorting functions
 */
export interface SortableElement {
  tag_name: string;
  is_required?: boolean | null;
  display_order?: number | null;
}

/**
 * Element type for content extraction
 */
export interface ContentElement {
  example_value?: string | null;
  alternate_example_value?: string | null;
  tag_name: string;
}

/**
 * Extract text content from example_value XML if present.
 * Returns the inner text of XML tags, or the raw value if not XML.
 * 
 * @param exampleValue - The example_value from adf_elements
 * @returns Extracted text content or null
 * 
 * @example
 * extractExampleContent('<name part="full">John Doe</name>') // Returns 'John Doe'
 * extractExampleContent('Plain text') // Returns 'Plain text'
 * extractExampleContent('<tag/>') // Returns null (self-closing)
 */
export function extractExampleContent(exampleValue: string | null | undefined): string | null {
  if (!exampleValue) return null;
  
  const trimmed = exampleValue.trim();
  
  // Check if it's XML-like content
  if (trimmed.startsWith('<')) {
    // Try to extract inner content between tags
    const innerMatch = trimmed.match(/>([^<]+)</);
    if (innerMatch && innerMatch[1].trim()) {
      return innerMatch[1].trim();
    }
    // Self-closing tag or empty content
    return null;
  }
  
  return trimmed;
}

/**
 * Build attribute string from element.attributes JSONB.
 * Uses database defaults - NO hardcoded values.
 * 
 * @param attributes - The attributes config from adf_elements
 * @param options - Options for attribute generation
 * @returns Formatted attribute string (e.g., 'type="individual" part="full"')
 * 
 * @example
 * buildAttributeString({ type: { default: 'individual' } }) // Returns 'type="individual"'
 * buildAttributeString({ interest: { default: 'buy' } }, { vehicleInterest: 'trade-in' }) // Returns 'interest="trade-in"'
 */
export function buildAttributeString(
  attributes: Record<string, AttributeConfig> | null,
  options: AttributeOptions = {}
): string {
  if (!attributes) return '';
  
  const {
    includeOptional = true,
    useMergeFields = false,
    elementSlug = '',
    vehicleInterest,
    isCoBuyer,
  } = options;
  
  const parts: string[] = [];
  
  for (const [name, config] of Object.entries(attributes)) {
    // Skip non-object configs (shouldn't happen but defensive)
    if (typeof config !== 'object' || config === null) continue;
    
    const isRequired = config.required === true;
    
    // Always include status and interest when vehicleInterest is provided
    const isVehicleContextAttribute = (name === 'status' || name === 'interest') && vehicleInterest;
    
    // Always include contact context attributes (primarycontact, preferredcontact, type)
    // These are critical for buyer/co-buyer differentiation
    const isContactContextAttribute = (name === 'primarycontact' || name === 'preferredcontact' || name === 'type');
    
    // Skip optional attributes UNLESS it's a critical context attribute
    if (!isRequired && !includeOptional && !isVehicleContextAttribute && !isContactContextAttribute) continue;
    
    // Determine the value
    let value: string;
    
    if (useMergeFields) {
      // Merge field mode: use placeholder
      const fieldName = `${elementSlug}_${name}`.replace(/-/g, '_');
      value = `{{${fieldName}}}`;
    } else {
      // Get value from config (database-driven)
      value = config.default || config.values?.[0] || '';
      
      // Instance-specific overrides for repeated elements
      if (name === 'interest' && vehicleInterest) {
        value = vehicleInterest;
      }
      // Set status based on vehicle interest (new for buy, used for trade-in)
      if (name === 'status' && vehicleInterest) {
        value = vehicleInterest === 'buy' ? 'new' : 'used';
      }
      if (name === 'primarycontact' && isCoBuyer) {
        value = '0';
      }
      if (name === 'preferredcontact' && isCoBuyer) {
        value = '0';
      }
      // Set type to co-buyer for co-buyer contacts
      if (name === 'type' && isCoBuyer) {
        value = 'co-buyer';
      }
    }
    
    if (value) {
      parts.push(`${name}="${value}"`);
    }
  }
  
  return parts.join(' ');
}

/**
 * Get sort priority for ADF element ordering.
 * Per ADF spec: id first, requestdate, vehicle, customer, vendor, provider, leadtype last
 * 
 * @param tagName - The element tag name
 * @returns Priority number (lower = higher priority)
 */
export function getElementSortPriority(tagName: string): number {
  const priorities: Record<string, number> = {
    'id': 0,
    'requestdate': 1,
    'vehicle': 2,
    'customer': 3,
    'vendor': 4,
    'provider': 5,
    'leadtype': 6, // leadtype goes at the END per ADF spec
  };
  return priorities[tagName] ?? 50; // Unknown elements in the middle
}

/**
 * Get example content from an element.
 * Uses alternate_example_value when useAlternate is true and the alternate exists.
 * 
 * @param element - The element with example values
 * @param options - Options for content selection
 * @returns Extracted example content or null
 */
export function getExampleContent(
  element: ContentElement,
  options: ExampleContentOptions = {}
): string | null {
  const { useAlternate = false } = options;
  
  // Use alternate if requested and available
  const sourceValue = useAlternate && element.alternate_example_value
    ? element.alternate_example_value
    : element.example_value;
  
  return extractExampleContent(sourceValue);
}

/**
 * Get the raw example value (with XML markup) from an element.
 * Uses alternate_example_value when useAlternate is true and the alternate exists.
 * 
 * @param element - The element with example values
 * @param options - Options for content selection
 * @returns Raw example value or null
 */
export function getRawExampleValue(
  element: ContentElement,
  options: ExampleContentOptions = {}
): string | null {
  const { useAlternate = false } = options;
  
  // Use alternate if requested and available
  if (useAlternate && element.alternate_example_value) {
    return element.alternate_example_value;
  }
  
  return element.example_value ?? null;
}

/**
 * Sort elements by ADF spec priority and display_order.
 * 
 * @param elements - Array of elements to sort
 * @returns Sorted copy of the elements array
 */
export function sortElementsByPriority<T extends SortableElement>(elements: T[]): T[] {
  return [...elements].sort((a, b) => {
    // First by priority (id, requestdate, etc.)
    const priorityDiff = getElementSortPriority(a.tag_name) - getElementSortPriority(b.tag_name);
    if (priorityDiff !== 0) return priorityDiff;
    
    // Then required first
    const aRequired = a.is_required ?? false;
    const bRequired = b.is_required ?? false;
    if (aRequired !== bRequired) return aRequired ? -1 : 1;
    
    // Finally by display_order
    return (a.display_order ?? 999) - (b.display_order ?? 999);
  });
}

/**
 * Check if an element is in a trade-in context based on its slug.
 * 
 * @param slug - The element slug
 * @param alternateContext - The alternate_context value from the element
 * @returns True if the element has trade-in alternate values
 */
export function hasTradeInAlternate(alternateContext: string | null | undefined): boolean {
  return alternateContext === 'trade-in';
}

/**
 * Check if an element is in a co-buyer context based on its alternate_context.
 * 
 * @param alternateContext - The alternate_context value from the element
 * @returns True if the element has co-buyer alternate values
 */
export function hasCoBuyerAlternate(alternateContext: string | null | undefined): boolean {
  return alternateContext === 'co-buyer';
}

/**
 * Format attributes for XML output with proper spacing.
 * 
 * @param attributeString - The attribute string from buildAttributeString
 * @returns Formatted string with leading space if not empty
 */
export function formatAttributesForXml(attributeString: string): string {
  if (!attributeString) return '';
  return ` ${attributeString}`;
}
